import { Fragment } from 'react';
import { Badge, Button, Col, Form, Overlay, Popover, Row, Table } from 'react-bootstrap';
import Select from 'react-select';

import { BotaoAcaoLinha } from '../../components/BotaoAcaoLinha';
import { BotaoAdicionar } from '../../components/RelatorioCivelComponents';
import type { AutorNaturezaItem, NaturezaAcaoOption, RelatorioCivelFormData } from '../relatorio-civel-page.models';
import type { RelatorioCivelStepProps } from './relatorio-civel-step-props';

export default function Step02AutorNatureza(props: RelatorioCivelStepProps) {
  const {
    novoAutor,
    setNovoAutor,
    adicionarAutorNatureza,
    naturezaAcaoOptions,
    novaNatureza,
    setNovaNatureza,
    isViewMode,
    quantidadeLinhasAutor,
    setQuantidadeLinhasAutor,
    adicionarMultiplasLinhasAutor,
    formData,
    editandoAutorNatureza,
    confirmandoExclusaoAutor,
    excluirAutorButtonRefs,
    setConfirmandoExclusaoAutor,
    confirmarExclusaoAutor,
    valorEdicaoAutor,
    setValorEdicaoAutor,
    valorEdicaoNatureza,
    setValorEdicaoNatureza,
    iniciarEdicaoAutorNatureza,
    salvarEdicaoAutorNatureza,
    cancelarEdicaoAutorNatureza,
    setFormData,
    RELATORIO_CORES,
  } = props;

  return (
    <Fragment>
      {!isViewMode && <Row className="mb-4">
        <Col md={5}>
          <Form.Group>
            <Form.Label className="fw-bold">Autor</Form.Label>
            <Form.Control
              type="text"
              placeholder="Nome do autor"
              value={novoAutor}
              onChange={(e) => setNovoAutor(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  adicionarAutorNatureza();
                }
              }}
              style={{ borderRadius: '8px' }}
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label className="fw-bold">Natureza da Ação</Form.Label>
            <Select
              classNamePrefix="react-select"
              value={naturezaAcaoOptions.find((o: NaturezaAcaoOption) => o.value === novaNatureza) || null}
              onChange={(option) => setNovaNatureza(option ? (option as NaturezaAcaoOption).value : '')}
              options={naturezaAcaoOptions}
              placeholder="Digite para buscar..."
              isClearable
              isDisabled={isViewMode}
            />
          </Form.Group>
        </Col>
        <Col md={3} className="d-flex align-items-end">
          <BotaoAdicionar className="w-100" onClick={adicionarAutorNatureza} />
        </Col>
      </Row>
      }

      <Row className="mb-4">
        <Col md={6}>
          {!isViewMode && <div className="d-flex align-items-center gap-2">
            <span className="text-muted">Adicionar</span>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setQuantidadeLinhasAutor((prev: number) => Math.max(1, prev - 1))}
              style={{ borderRadius: '8px', width: '32px', height: '32px', padding: 0 }}
            >
              <i className="bi bi-dash">-</i>
            </Button>
            <Form.Control
              type="number"
              min="1"
              max="50"
              value={quantidadeLinhasAutor}
              onChange={(e) => setQuantidadeLinhasAutor(Math.max(1, parseInt(e.target.value) || 1))}
              style={{ width: '60px', textAlign: 'center', borderRadius: '8px' }}
              size="sm"
            />
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setQuantidadeLinhasAutor((prev: number) => Math.min(50, prev + 1))}
              style={{ borderRadius: '8px', width: '32px', height: '32px', padding: 0 }}
            >
              <i className="bi bi-plus">+</i>
            </Button>
            <Button variant="outline-primary" size="sm" onClick={adicionarMultiplasLinhasAutor} style={{ borderRadius: '8px' }}>
              <i className="bi bi-plus-lg me-1"></i>
              Adicionar {quantidadeLinhasAutor} linha{quantidadeLinhasAutor > 1 ? 's' : ''}
            </Button>
          </div>}
        </Col>
        <Col md={6} className="d-flex align-items-center justify-content-end">
          <h5 className="mb-0" style={{ color: RELATORIO_CORES.PRIMARIA }}>
            <small className="text-muted">Total de Autores:</small> {formData.autorNatureza.itens.length}
          </h5>
        </Col>
      </Row>

      {formData.autorNatureza.itens.length === 0 ? (
        <div className="text-center text-muted py-4 border rounded">
          <i className="bi bi-inbox fs-1"></i>
          <p className="mb-0 mt-2">Nenhum autor adicionado ainda.</p>
          <small>Adicione autores e natureza das ações acima.</small>
        </div>
      ) : (
        <Table striped bordered hover responsive>
          <thead className="table-light">
            <tr>
              <th style={{ width: '50px' }}>#</th>
              <th>Autor</th>
              <th style={{ width: '200px' }}>Natureza da Ação</th>
              {!isViewMode && <th style={{ width: '200px' }}>Ações</th>}
            </tr>
          </thead>
          <tbody>
            {formData.autorNatureza.itens.map((item: AutorNaturezaItem, index: number) => {
              const estaEditando = editandoAutorNatureza === item.id;
              const estaConfirmandoExclusao = confirmandoExclusaoAutor === item.id;
              const refKey = `autor-${item.id}`;

              return (
                <tr key={item.id}>
                  <td>{index + 1}</td>
                  <td>
                    {estaEditando ? (
                      <Form.Control
                        type="text"
                        size="sm"
                        value={valorEdicaoAutor}
                        onChange={(e) => setValorEdicaoAutor(e.target.value)}
                        style={{ borderRadius: '8px' }}
                        autoFocus
                      />
                    ) : item.autor ? (
                      <span>{item.autor}</span>
                    ) : (
                      <Form.Control
                        type="text"
                        size="sm"
                        placeholder="Digite o nome do autor"
                        style={{ borderRadius: '8px' }}
                        onBlur={(e) => {
                          if (e.target.value.trim()) {
                            setFormData((prev: RelatorioCivelFormData) => ({
                              ...prev,
                              autorNatureza: {
                                itens: prev.autorNatureza.itens.map((i: AutorNaturezaItem) =>
                                  i.id === item.id ? { ...i, autor: e.target.value.trim() } : i,
                                ),
                              },
                            }));
                          }
                        }}
                      />
                    )}
                  </td>
                  <td>
                    {estaEditando ? (
                      <Form.Select
                        size="sm"
                        value={valorEdicaoNatureza}
                        onChange={(e) => setValorEdicaoNatureza(e.target.value)}
                        style={{ borderRadius: '8px' }}
                      >
                        {naturezaAcaoOptions.map((option: NaturezaAcaoOption) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Form.Select>
                    ) : item.naturezaAcao ? (
                      <Badge style={{ backgroundColor: RELATORIO_CORES.PRIMARIA }}>
                        {naturezaAcaoOptions.find((o: NaturezaAcaoOption) => o.value === item.naturezaAcao)?.label || item.naturezaAcao}
                      </Badge>
                    ) : (
                      <Form.Select
                        size="sm"
                        style={{ borderRadius: '8px' }}
                        onChange={(e) => {
                          if (e.target.value) {
                            setFormData((prev: RelatorioCivelFormData) => ({
                              ...prev,
                              autorNatureza: {
                                itens: prev.autorNatureza.itens.map((i: AutorNaturezaItem) =>
                                  i.id === item.id ? { ...i, naturezaAcao: e.target.value } : i,
                                ),
                              },
                            }));
                          }
                        }}
                      >
                        {naturezaAcaoOptions.map((option: NaturezaAcaoOption) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Form.Select>
                    )}
                  </td>
                  {!isViewMode && <td>
                    {estaEditando ? (
                      <div className="d-flex align-items-center justify-content-center flex-nowrap gap-1">
                        <BotaoAcaoLinha tipo="salvar" onClick={() => salvarEdicaoAutorNatureza(item.id)} />
                        <BotaoAcaoLinha tipo="cancelar" onClick={cancelarEdicaoAutorNatureza} />
                      </div>
                    ) : (
                      <div className="d-flex align-items-center justify-content-center flex-nowrap gap-1">
                        <BotaoAcaoLinha tipo="editar" onClick={() => iniciarEdicaoAutorNatureza(item)} />
                        <BotaoAcaoLinha
                          ref={(el) => {
                            excluirAutorButtonRefs.current[refKey] = el;
                          }}
                          tipo="excluir"
                          onClick={() => setConfirmandoExclusaoAutor(item.id)}
                        />
                        <Overlay
                          show={estaConfirmandoExclusao}
                          target={excluirAutorButtonRefs.current[refKey]}
                          placement="top"
                          containerPadding={20}
                        >
                          <Popover id={`popover-delete-autor-${refKey}`}>
                            <Popover.Header as="h3" className="text-center">
                              Deseja mesmo excluir?
                            </Popover.Header>
                            <Popover.Body className="d-flex gap-2 justify-content-center">
                              <Button variant="danger" size="sm" onClick={() => confirmarExclusaoAutor(item.id)}>
                                Sim
                              </Button>
                              <Button variant="secondary" size="sm" onClick={() => setConfirmandoExclusaoAutor(null)}>
                                Não
                              </Button>
                            </Popover.Body>
                          </Popover>
                        </Overlay>
                      </div>
                    )}
                  </td>}
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </Fragment>
  );
}

